import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  acceptanceInputFor,
  useAcceptSuccessFeeTerms,
} from '@/hooks/actions/use-accept-success-fee-terms';
import { formatLongDate } from '@/lib/format-long-date';
import type { SuccessFeeTermsDocument } from '@fluenceur/types';
import { Button, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { FrozenTermsBody } from './FrozenTermsBody';

export interface SuccessFeeTermsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: SuccessFeeTermsDocument | null;
  /** Only a brand owner or admin engages the organization. */
  canAccept: boolean;
  /**
   * Fired on a recorded acceptance, before the dialog closes.
   *
   * Closing alone cannot stand in for it: a reader who gives up closes the same
   * dialog, and a caller resuming on that would act on a refusal.
   */
  onAccepted?: () => void;
  /** One line saying why the acceptance is asked for here. */
  helper?: string;
  /** The document is still being read; nothing is offered until it is on screen. */
  isLoading?: boolean;
}

/**
 * Reads the frozen terms, and accepts them for the organization.
 *
 * The acceptance echoes the locale and hash of the snapshot on screen, never a
 * value computed here: the proof has to name the text the person read. A member
 * with view access gets the same document and no action.
 */
export const SuccessFeeTermsDialog: React.FC<SuccessFeeTermsDialogProps> = ({
  isOpen,
  onClose,
  document,
  canAccept,
  onAccepted,
  helper,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const accept = useAcceptSuccessFeeTerms();

  const handleAccept = () => {
    if (!document) return;

    // A refusal is already surfaced by the mutation's own toast, and leaves the
    // dialog open so the reader can retry against the text still on screen.
    accept.mutate(acceptanceInputFor(document), {
      onSuccess: () => {
        onAccepted?.();
        onClose();
      },
    });
  };

  // Nothing is claimed about the reader's rights until the answer is in: a
  // helper saying only an administrator can accept, shown for a moment and then
  // replaced by the accept button, reads as a refusal that never happened.
  const isAcceptanceOffered = canAccept && !isLoading;

  const effectiveAt = formatLongDate(document?.effectiveAt, t('common.locale'));

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
      <DialogContent maxW={{ base: '100vw', md: '860px' }} maxH="90vh">
        <DialogHeader>
          <VStack align="flex-start" gap={1}>
            <DialogTitle>{t('legal.successFeeTerms.document.title')}</DialogTitle>
            {effectiveAt && (
              <Text fontSize="sm" color="fg.muted">
                {t('legal.successFeeTerms.document.effectiveAt', { date: effectiveAt })}
              </Text>
            )}
          </VStack>
        </DialogHeader>

        <DialogBody overflowY="auto" maxH="calc(90vh - 220px)">
          {helper && (
            <Text fontSize="sm" color="fg.muted" mb={4}>
              {helper}
            </Text>
          )}
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner size="md" color="fg.muted" />
            </Flex>
          ) : (
            <FrozenTermsBody document={document} />
          )}
        </DialogBody>

        <DialogFooter>
          <HStack gap={3}>
            {!canAccept && !isLoading && (
              <Text fontSize="sm" color="fg.muted">
                {t('legal.successFeeTerms.viewerHelper')}
              </Text>
            )}
            <Button onClick={onClose} variant="outline">
              {t('legal.successFeeTerms.document.close')}
            </Button>
            {isAcceptanceOffered && (
              <Button
                onClick={handleAccept}
                colorPalette="primary"
                loading={accept.isPending}
                loadingText={t('legal.successFeeTerms.accept.pending')}
                disabled={!document}
              >
                {t('legal.successFeeTerms.accept.action')}
              </Button>
            )}
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
